export interface GalleryPhoto {
  image?: string | null;
  alt_text?: string | null;
  title?: string | null;
  description?: string | null;
  source_page_urls?: string | null;
  original_url?: string | null;
}

/** Category titles that are not real marketing photos. */
const EXCLUDED_CATEGORY_TITLES = new Set(['other', 'floorplan', 'logo']);

/**
 * Hard exclusions — never claim these as interior page heroes.
 */
const UNDESIRABLE_KEYWORDS = [
  'bathroom',
  'bath room',
  'master bath',
  'guest bath',
  'full bath',
  'half bath',
  'restroom',
  'toilet',
  'parking lot',
  'parking garage',
  'parking space',
  'carport',
  'dumpster',
  'trash',
  'garbage',
  'recycling bin',
  'mechanical',
  'utility closet',
  'hvac',
  'site plan',
  'floor plan',
  'floorplan',
  'logo',
  'map pin',
  'qr code',
];

/** Soft penalties — prefer something else when available. */
const LOW_VALUE_KEYWORDS = [
  'parking',
  'garage',
  'laundry',
  'washer',
  'dryer',
  'closet',
  'hallway',
  'corridor',
  'stair',
  'elevator',
  'mailbox',
  'office desk',
  'empty room',
];

/**
 * Standout quality signals — used as a baseline so weak page matches still
 * land on a strong photo rather than a random gallery index.
 */
const STANDOUT_KEYWORDS = [
  'staged',
  'staging',
  'furnished',
  'living room',
  'living',
  'great room',
  'kitchen',
  'dining',
  'bedroom',
  'exterior',
  'building',
  'entrance',
  'entry',
  'lobby',
  'courtyard',
  'pool',
  'clubhouse',
  'lounge',
  'fitness',
  'gym',
  'aerial',
  'drone',
  'skyline',
  'patio',
  'balcony',
  'amenity',
  'community',
];

/**
 * Primary interior pages — claim unique gallery photos first, in this order.
 * Earlier pages win when two pages want the same standout shot.
 */
const PRIMARY_PAGE_ORDER = [
  'floor-plans-hero',
  'amenities-hero',
  'neighborhood-hero',
  'gallery-hero',
  'virtual-tour-hero',
  'contact-hero',
  'schedule-tour-hero',
  'pet-policy-hero',
  'interactive-map-hero',
  'reviews-hero',
  'apply-now-hero',
  'resident-access-hero',
  'faq-hero',
  'preferred-employer-hero',
  'sister-properties-hero',
  'studios-hero',
  '1-bedrooms-hero',
  '2-bedroom-hero',
  'townhomes-hero',
] as const;

/**
 * Utility / legal pages — use leftovers only; otherwise keep the featured
 * image so primary pages can exhaust the good gallery shots uniquely.
 */
const FEATURED_FALLBACK_PAGES = new Set([
  'privacy-policy-hero',
  'sitemap-hero',
  'accessibility-hero',
  'thank-you-hero',
  'fair-housing-hero',
]);

const FEATURED_FALLBACK_ORDER = [
  'thank-you-hero',
  'privacy-policy-hero',
  'sitemap-hero',
  'accessibility-hero',
  'fair-housing-hero',
] as const;

/**
 * Page-specific preference keywords keyed by HeroSolidBG `tokenPrefix`.
 */
const PAGE_KEYWORDS: Record<string, string[]> = {
  'floor-plans-hero': [
    'staged',
    'staging',
    'furnished',
    'apartment',
    'unit',
    'interior',
    'living room',
    'living',
    'kitchen',
    'bedroom',
    'dining',
    'great room',
    'open concept',
  ],
  'amenities-hero': [
    'pool',
    'fitness',
    'gym',
    'clubhouse',
    'lounge',
    'amenity',
    'amenities',
    'courtyard',
    'grill',
    'bbq',
    'spa',
    'rooftop',
    'recreation',
    'community',
  ],
  'pet-policy-hero': [
    'pet',
    'dog',
    'dog park',
    'pet spa',
    'pet wash',
    'dog wash',
    'animal',
  ],
  'gallery-hero': [
    'exterior',
    'building',
    'staged',
    'living',
    'pool',
    'community',
    'aerial',
  ],
  'neighborhood-hero': [
    'exterior',
    'aerial',
    'drone',
    'skyline',
    'neighborhood',
    'street',
    'building',
    'entrance',
    'location',
    'surrounding',
  ],
  'virtual-tour-hero': [
    'staged',
    'staging',
    'furnished',
    'apartment',
    'interior',
    'living',
    'kitchen',
    'bedroom',
    'tour',
  ],
  'interactive-map-hero': [
    'aerial',
    'drone',
    'exterior',
    'building',
    'map',
    'community',
    'entrance',
  ],
  'contact-hero': [
    'exterior',
    'entrance',
    'entry',
    'lobby',
    'leasing',
    'office',
    'building',
    'community',
  ],
  'schedule-tour-hero': [
    'exterior',
    'entrance',
    'lobby',
    'building',
    'community',
    'courtyard',
    'staged',
  ],
  'resident-access-hero': [
    'exterior',
    'entrance',
    'lobby',
    'building',
    'community',
  ],
  'apply-now-hero': [
    'exterior',
    'building',
    'staged',
    'living',
    'community',
    'entrance',
  ],
  'reviews-hero': [
    'exterior',
    'community',
    'pool',
    'courtyard',
    'lounge',
    'building',
  ],
  'faq-hero': [
    'exterior',
    'community',
    'building',
    'entrance',
  ],
  'preferred-employer-hero': [
    'exterior',
    'building',
    'community',
    'entrance',
    'lounge',
  ],
  'sister-properties-hero': [
    'exterior',
    'building',
    'community',
    'aerial',
  ],
  'studios-hero': [
    'studio',
    'staged',
    'staging',
    'furnished',
    'open concept',
    'open-concept',
    'apartment',
    'unit',
    'interior',
    'living',
    'kitchen',
  ],
  '1-bedrooms-hero': [
    '1 bedroom',
    'one bedroom',
    'bedroom',
    'staged',
    'staging',
    'furnished',
    'apartment',
    'unit',
    'interior',
    'living',
    'kitchen',
  ],
  '2-bedroom-hero': [
    '2 bedroom',
    'two bedroom',
    'bedroom',
    'staged',
    'staging',
    'furnished',
    'apartment',
    'unit',
    'interior',
    'living',
    'kitchen',
    'dining',
  ],
  'townhomes-hero': [
    'townhome',
    'townhouse',
    'town home',
    'town house',
    'exterior',
    'building',
    'entrance',
    'entry',
    'staged',
    'staging',
    'furnished',
    'living',
    'kitchen',
  ],
};

function getUrlPath(url: string | null | undefined): string {
  if (!url) return '';

  try {
    return decodeURIComponent(new URL(url).pathname);
  } catch {
    return '';
  }
}

/** Normalize CDN variants so the same asset isn't assigned to two pages. */
function photoIdentity(photo: GalleryPhoto): string {
  const url = photo.image || photo.original_url || '';
  if (!url) return '';

  try {
    const parsed = new URL(url);
    return `${parsed.origin}${parsed.pathname}`.replace(/\/+$/, '').toLowerCase();
  } catch {
    return url.toLowerCase().split('?')[0].replace(/\/+$/, '');
  }
}

function photoText(photo: GalleryPhoto): string {
  const parts = [
    photo.title,
    photo.description,
    photo.alt_text,
    getUrlPath(photo.image),
    getUrlPath(photo.original_url),
    photo.source_page_urls,
  ];

  return parts
    .filter((part): part is string => Boolean(part))
    .join(' ')
    .toLowerCase();
}

function hasKeyword(text: string, keyword: string): boolean {
  return text.includes(keyword);
}

function scoreKeywords(text: string, keywords: string[], weight = 1): number {
  return keywords.reduce(
    (score, keyword) => score + (hasKeyword(text, keyword) ? weight : 0),
    0,
  );
}

function isExcludedCategory(photo: GalleryPhoto): boolean {
  const title = (photo.title || '').toLowerCase().trim();
  return EXCLUDED_CATEGORY_TITLES.has(title);
}

function hasPhotoUrl(photo: GalleryPhoto): boolean {
  return Boolean(photo.image || photo.original_url);
}

function pageKeywordsFor(preset: string): string[] {
  return PAGE_KEYWORDS[preset] || STANDOUT_KEYWORDS;
}

/**
 * Score a gallery photo for use as an interior-page hero.
 * Higher is better. Undesirable photos return a large negative score.
 */
export function scoreHeroPhoto(
  photo: GalleryPhoto,
  pageKeywords: string[],
): number {
  if (!hasPhotoUrl(photo) || isExcludedCategory(photo)) return -1000;

  const text = photoText(photo);
  if (!text.trim()) {
    // Untitled photos can still be fine — treat as neutral standout candidate.
    return 0;
  }

  if (UNDESIRABLE_KEYWORDS.some((keyword) => hasKeyword(text, keyword))) {
    return -500;
  }

  const pageScore = scoreKeywords(text, pageKeywords, 3);
  const standoutScore = scoreKeywords(text, STANDOUT_KEYWORDS, 1);
  const lowValuePenalty = scoreKeywords(text, LOW_VALUE_KEYWORDS, 2);

  const category = (photo.title || '').toLowerCase().trim();
  let categoryBoost = 0;
  if (
    category === 'apartment' &&
    pageKeywords.some(
      (k) => k.includes('apartment') || k.includes('living') || k.includes('kitchen'),
    )
  ) {
    categoryBoost = 2;
  } else if (
    category === 'community' &&
    pageKeywords.some(
      (k) => k.includes('community') || k.includes('pool') || k.includes('exterior'),
    )
  ) {
    categoryBoost = 2;
  }

  return pageScore + standoutScore + categoryBoost - lowValuePenalty;
}

function pickBestUnused(
  gallery: GalleryPhoto[],
  pageKey: string,
  usedIndices: Set<number>,
  usedUrls: Set<string>,
  minScore: number,
): number {
  const keywords = pageKeywordsFor(pageKey);
  let bestIndex = -1;
  let bestScore = minScore - 1;

  for (let index = 0; index < gallery.length; index += 1) {
    if (usedIndices.has(index)) continue;

    const identity = photoIdentity(gallery[index]);
    if (identity && usedUrls.has(identity)) continue;

    const score = scoreHeroPhoto(gallery[index], keywords);
    if (score < minScore) continue;
    if (score <= bestScore) continue;

    bestScore = score;
    bestIndex = index;
  }

  return bestIndex;
}

function claimPhoto(
  gallery: GalleryPhoto[],
  index: number,
  usedIndices: Set<number>,
  usedUrls: Set<string>,
): void {
  usedIndices.add(index);
  const identity = photoIdentity(gallery[index]);
  if (identity) usedUrls.add(identity);
}

/**
 * Deterministically assign a unique gallery index (or -1 = featured image)
 * to every known interior hero page. Safe to call per-page: the result for
 * a given gallery is always the same.
 */
export function assignHeroImages(
  gallery: GalleryPhoto[],
): Map<string, number> {
  const assignments = new Map<string, number>();
  if (!gallery.length) return assignments;

  const usedIndices = new Set<number>();
  const usedUrls = new Set<string>();

  for (const pageKey of PRIMARY_PAGE_ORDER) {
    // Accept neutral untitled photos (score 0) so sparse galleries still
    // get unique heroes before falling back to featured.
    const index = pickBestUnused(gallery, pageKey, usedIndices, usedUrls, 0);
    assignments.set(pageKey, index);
    if (index >= 0) claimPhoto(gallery, index, usedIndices, usedUrls);
  }

  // Leftovers only — otherwise these pages keep the featured image.
  for (const pageKey of FEATURED_FALLBACK_ORDER) {
    const index = pickBestUnused(gallery, pageKey, usedIndices, usedUrls, 1);
    assignments.set(pageKey, index);
    if (index >= 0) claimPhoto(gallery, index, usedIndices, usedUrls);
  }

  return assignments;
}

/**
 * Pick a gallery index for an interior page hero.
 *
 * Returns a unique index across pages when possible. Returns `-1` to signal
 * "use the property featured image" (utility pages, or when no unused
 * desirable photo remains).
 *
 * @param gallery Property photo gallery
 * @param keywordsOrPreset Page `tokenPrefix`, or legacy keyword list
 * @param preset Legacy preset when the second arg is a keyword list
 */
export function pickHeroImage(
  gallery: GalleryPhoto[],
  keywordsOrPreset: string[] | string,
  preset?: string,
): number {
  if (!gallery.length) return -1;

  const pageKey =
    typeof keywordsOrPreset === 'string' ? keywordsOrPreset : preset || '';

  // Legacy keyword-list callers: best unused match without cross-page map.
  if (Array.isArray(keywordsOrPreset)) {
    const usedIndices = new Set<number>();
    const usedUrls = new Set<string>();
    const keywords =
      keywordsOrPreset.length > 0 ? keywordsOrPreset : STANDOUT_KEYWORDS;
    let bestIndex = -1;
    let bestScore = -1;
    for (let index = 0; index < gallery.length; index += 1) {
      const score = scoreHeroPhoto(gallery[index], keywords);
      if (score <= bestScore) continue;
      bestScore = score;
      bestIndex = index;
    }
    if (bestIndex >= 0 && bestScore >= 0) {
      claimPhoto(gallery, bestIndex, usedIndices, usedUrls);
      return bestIndex;
    }
    return -1;
  }

  if (!pageKey) return -1;

  const assignments = assignHeroImages(gallery);

  if (assignments.has(pageKey)) {
    return assignments.get(pageKey)!;
  }

  // Unknown page: claim any remaining unused standout, else featured.
  if (FEATURED_FALLBACK_PAGES.has(pageKey)) {
    return -1;
  }

  const usedIndices = new Set<number>();
  const usedUrls = new Set<string>();
  for (const index of assignments.values()) {
    if (index >= 0) claimPhoto(gallery, index, usedIndices, usedUrls);
  }

  return pickBestUnused(gallery, pageKey, usedIndices, usedUrls, 0);
}
